import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useMe } from "./queries";
import { api } from "./api";
import { isNative } from "./native";
import { disableNativePush } from "./nativePush";
import { PageLoader, ErrorState } from "@/components/ui";
import { KidLocked } from "@/pages/kid/Locked";
import { AcceptTerms } from "@/pages/parent/AcceptTerms";
import type { FamilyDTO, MeDTO, MemberDTO } from "@shared/types";

export interface Session {
  me: MeDTO;
  user: MemberDTO;
  family: FamilyDTO;
  members: MemberDTO[];
  kids: MemberDTO[];
  parents: MemberDTO[];
  memberById: (id: string | null | undefined) => MemberDTO | undefined;
}

export function toSession(me: MeDTO): Session {
  return {
    me,
    user: me.user,
    family: me.family,
    members: me.members,
    kids: me.members.filter((m) => m.role === "kid"),
    parents: me.members.filter((m) => m.role === "parent"),
    memberById: (id) => me.members.find((m) => m.id === id),
  };
}

/** Use inside a route guarded by <RequireRole>. */
export function useSession(): Session {
  const { data } = useMe();
  if (!data) throw new Error("useSession used outside an authenticated route");
  return toSession(data);
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    // Needs the session, and a shared phone shouldn't keep getting this parent's notifications.
    if (isNative()) await disableNativePush();
    await api.post("/auth/logout").catch(() => {});
    qc.setQueryData(["me"], null);
    qc.clear();
  };
}

export function RequireRole({ role, children }: { role: "parent" | "kid"; children: ReactNode }) {
  const { data, isLoading, error, refetch } = useMe();
  const location = useLocation();
  if (isLoading) return <PageLoader />;
  if (error) return <div className="p-6"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (!data) return <Navigate to={role === "kid" ? "/kid-login" : "/login"} replace state={{ from: location.pathname + location.search }} />;
  if (data.user.role !== role) return <Navigate to={data.user.role === "parent" ? "/parent" : "/kid"} replace />;
  // Updated Terms / Privacy Policy: parents accept before anything else. Kids need a parent's consent.
  if (role === "parent" && data.privacy.mustAcceptLegal) return <AcceptTerms />;
  if (role === "kid" && data.privacy.consent !== "granted") return <KidLocked reason="consent" />;
  // No trial or subscription yet (or it lapsed): parents choose a plan first, kids wait for them.
  if (data.billing.enabled && !data.billing.active) {
    if (role === "kid") return <KidLocked />;
    if (!location.pathname.startsWith("/parent/billing")) return <Navigate to="/parent/billing" replace />;
  }
  if (
    role === "parent" &&
    !data.family.onboarded &&
    !location.pathname.startsWith("/parent/setup") &&
    !location.pathname.startsWith("/parent/billing") &&
    !location.pathname.startsWith("/parent/privacy") &&
    !location.pathname.startsWith("/connect")
  ) {
    return <Navigate to="/parent/setup" replace />;
  }
  return <>{children}</>;
}
